import { isEmpty, result } from 'underscore';
import { surveyStatus } from './survey-status';
import { SurveyPreferenceService } from './survey-preference-service';

export class BaseSurvey {
  /*
  * @param {props} all context needed for the survey. E.g. serverContext, preferenceService
  */
  constructor(props) {
    this.serverContext = props.serverContext;
    this.SurveyPreferenceService = new SurveyPreferenceService({
      PreferenceService: props.PreferenceService,
    });
    this.logActivity = props.logActivity;

    this.isTestEnv = !result(this.serverContext, 'IsProdEnv') && !window.location.href.toLowerCase().includes('ads.microsoft.com');
  }

  /*
  * @return {React.Component} React component of the survey view
  */
  getSurveyComponent = () => {
    throw new Error('Method not implemented');
  };

  /*
  * @param {isTestEnv} indicate if a survey is in the test environment
  * @return {string} the OCV survey Id where the feedback sent to
  */
  getSurveyId = (/* isTestEnv */) => {
    throw new Error('Method not implemented');
  };

  /*
  * @return {string} name of the survey. This is used for logging
  */
  getSurveyName = () => {
    throw new Error('Method not implemented');
  };

  /*
  * Survey should not change their priority without an exception
  * @return {number} priority of the survey. The larger the number, the higher the priority
  */
  getSurveyPriority = () => 100;

  /*
  * validate the trigger conditions of the survey
  * @return {bool} indicate if the survey is enabled to trigger
  */
  validateTriggerCondition = () => {
    throw new Error('Method not implemented');
  };

  /*
  * Return the custom params of the survey. E.g. { event: xxx, sku: xxx }
  * The params will be sent to the OCV. You can find it in the App Data Fields along with the feedback.
  * @return {object} custom params (key-value pairs) of the survey to be sent to the OCV survey
  */
  getSurveyCustomParams = () => ({});

  /*
  * Return the initializationConfig of the survey. This is used to initialize the survey preference
  * @return {object} initializationConfig of the survey
  * @return {string} initializationConfig.initGroup the group count to split with
  * @return {string} initializationConfig.initStartDate the start date of the initialization
  * @return {string} initializationConfig.initEndDate the end date of the initialization
  */
  getInitializationConfig = () => ({});

  /*
  * validate the user governance of the survey, e.g. whether the user passes the cooldown period
  * @return {bool} indicate if the survey is enabled to trigger
  */
  validateUserGovernance = () => {
    if (this.isTestEnv) {
      this.logActivity(`User Governance | survey: ${this.getSurveyName()}, isTestEnv: ${this.isTestEnv}`);
      return true;
    }

    const checkSurveyState = (surveyState) => {
      // check whether current time exceeds the survey next shown date, show the survey if it is
      const isSurveyShown = Date.now() > new Date(surveyState.nextShownDate);
      this.logActivity(`User Governance | survey: ${this.getSurveyName()}, isSurveyShown: ${isSurveyShown}, Now: ${Date()}, NextShownDate: ${surveyState.nextShownDate}, Status: ${surveyState.status}`);
      return isSurveyShown;
    };

    const surveyState = this.SurveyPreferenceService.getSurveyState({
      surveyName: this.getSurveyName(),
    });

    if (!isEmpty(surveyState) && surveyState.nextShownDate != null) {
      return checkSurveyState(surveyState);
    }

    // not setup the survey preference for this survey yet, initialize the survey preference
    const initializationConfig = this.getInitializationConfig();
    if (!isEmpty(initializationConfig)) {
      // get initializationConfig, initialize the survey preference
      if (initializationConfig.initGroup && initializationConfig.initStartDate && initializationConfig.initEndDate) {
        const { initGroup } = initializationConfig;
        const userGroup = result(this.serverContext, ['CurrentUser', 'Id']) % initGroup;
        const initStartDate = new Date(initializationConfig.initStartDate);
        const initEndDate = new Date(initializationConfig.initEndDate);
        const initUnit = ((initEndDate - initStartDate) / 86400000) / initGroup;

        const newSurveyState = {
          status: surveyStatus.INIT,
          nextShownDate: this.SurveyPreferenceService.getDaysAfter(Math.floor(userGroup * initUnit), initializationConfig.initStartDate),
        };
        this.SurveyPreferenceService.setSurveyState({
          surveyName: this.getSurveyName(),
          surveyState: newSurveyState,
        });
        this.logActivity(`User Governance | survey: ${this.getSurveyName()}, Initialization success, NextShownDate: ${newSurveyState.nextShownDate}, Status: ${newSurveyState.status}`);

        return checkSurveyState(newSurveyState);
      }

      this.logActivity(`User Governance | survey: ${this.getSurveyName()}, Initialization failed, initGroup: ${initializationConfig.initGroup}, initStartDate: ${initializationConfig.initStartDate}, initEndDate: ${initializationConfig.initEndDate}`);
      throw new Error(`Initialization failed, initGroup: ${initializationConfig.initGroup}, initStartDate: ${initializationConfig.initStartDate}, initEndDate: ${initializationConfig.initEndDate}`);
    } else {
      // bypass the initialization, show the survey
      this.logActivity(`User Governance | survey: ${this.getSurveyName()}, Bypass initialization`);
      return true;
    }
  };

  /*
  * Callback called when rending a survey, update the survey trigger condition / user governance
  */
  onSurveyRender = () => {
    /* not implemented */
  };

  /*
  * Callback called when submitting a survey, update the survey trigger condition / user governance
  */
  onSurveySubmit() {
    const newSurveyState = {
      status: surveyStatus.SUBMIT,
      nextShownDate: this.SurveyPreferenceService.getDaysAfter(180),
    };
    this.SurveyPreferenceService.setSurveyState({
      surveyName: this.getSurveyName(),
      surveyState: newSurveyState,
    });
  }

  /*
  * Callback called when dismissing a survey, update the survey trigger condition / user governance
  */
  onSurveyDismiss() {
    const surveyState = this.SurveyPreferenceService.getSurveyState({
      surveyName: this.getSurveyName(),
    });

    let newSurveyState = {};
    if (!isEmpty(surveyState)) {
      const { status } = surveyState;
      switch (status) {
        case surveyStatus.FIRST_DISMISS:
          // set new state to second-dismiss and next shown date to 14 days
          newSurveyState = {
            status: surveyStatus.SECOND_DISMISS,
            nextShownDate: this.SurveyPreferenceService.getDaysAfter(14),
          };
          break;
        case surveyStatus.SECOND_DISMISS:
          // set new state to third-dismiss and next shown date to 30 days
          newSurveyState = {
            status: surveyStatus.THIRD_DISMISS,
            nextShownDate: this.SurveyPreferenceService.getDaysAfter(30),
          };
          break;
        case surveyStatus.THIRD_DISMISS:
          // set new state to fourth-dismiss and next shown date to 180 days
          newSurveyState = {
            status: surveyStatus.FOURTH_DISMISS,
            nextShownDate: this.SurveyPreferenceService.getDaysAfter(180),
          };
          break;
        case surveyStatus.INIT:
        case surveyStatus.SUBMIT:
        case surveyStatus.FOURTH_DISMISS:
        default:
          // new cycle, set new state to first-dismiss and next shown date to 7 days
          newSurveyState = {
            status: surveyStatus.FIRST_DISMISS,
            nextShownDate: this.SurveyPreferenceService.getDaysAfter(7),
          };
          break;
      }
    } else {
      // new cycle, set new state to first-dismiss and next shown date to 7 days
      newSurveyState = {
        status: surveyStatus.FIRST_DISMISS,
        nextShownDate: this.SurveyPreferenceService.getDaysAfter(7),
      };
    }

    this.SurveyPreferenceService.setSurveyState({
      surveyName: this.getSurveyName(),
      surveyState: newSurveyState,
    });
  }

  /*
  * @return {bool} whether the survey is onboarded to the CSAT reporting pipeline.
  * See the details of the pipeline here:
  * https://msasg.visualstudio.com/Bing_Ads/_wiki/wikis/Bing_Ads.wiki/122337/CSAT-Reporting
  */
  isReportingPipelineEnabled = () => false;

  /*
  * @return {string} product of the survey. This is used for the CSAT reporting pipeline.
  * Only works when isReportingPipelineEnabled is true.
  */
  getSurveyProduct = () => {
    if (this.isReportingPipelineEnabled()) {
      throw new Error('Method not implemented');
    }
  };

  /*
  * @return {string} interface of the survey. This is used for the CSAT reporting pipeline.
  * Only works when isReportingPipelineEnabled is true.
  */
  getSurveyInterface = () => {
    if (this.isReportingPipelineEnabled()) {
      throw new Error('Method not implemented');
    }
  };
}
